import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { NotificationBadge } from '@xing-com/badge';
import { navigate } from '@xing-com/crate-core-router';
import {
  ORIGIN_PARAM,
  ORIGIN_PARAM_VALUES,
  SEARCH_STATE_PARAMS,
} from '@xing-com/crate-jobs-constants';
import { SearchAlertFragment } from '@xing-com/crate-jobs-graphql';
import { useSearchAlerts } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';

import { Tag } from './promotions.styles';

const buildAlertUrl = (alert: SearchAlertFragment): string => {
  const params = new URLSearchParams({
    [SEARCH_STATE_PARAMS.sort]: 'date',
    [ORIGIN_PARAM]: ORIGIN_PARAM_VALUES.searchAlertsFindJobs,
    ...(alert.visitedAt &&
      alert.newJobCount && {
        [SEARCH_STATE_PARAMS.since]: alert.visitedAt,
      }),
    sc_o: 'jobs_search_alert',
    sc_o_PropActionOrigin: 'jobs_find_jobs',
  });

  return `${ROUTES.search}/${alert.queryId}?${params.toString()}`;
};

export const SearchAlertPromotions: React.FC = () => {
  const { data } = useSearchAlerts();
  const trackNwt = useJobsNwtTracking();

  if (data?.viewer?.searchAlerts?.__typename !== 'SearchAlertResultResponse') {
    return null;
  }

  const list = (data?.viewer?.searchAlerts.collection || [])
    .filter((alert): alert is SearchAlertFragment => {
      return typeof alert?.newJobCount === 'number' && alert?.newJobCount > 0;
    })
    .sort((a, b) => {
      return (b?.newJobCount ?? 0) - (a?.newJobCount ?? 0);
    })
    .slice(0, 2);

  const onClick = (
    alert: SearchAlertFragment | undefined,
    index: number
  ): void => {
    if (!alert) {
      return;
    }

    trackNwt({
      eventSchema: 'basic',
      event: 'opened',
      element: 'search_alert',
      itemUrn: alert?.globalId,
      page: 'jobs/index',
      position: index,
    });

    navigate(buildAlertUrl(alert));
  };

  return list?.map((alert, index) => (
    <SearchAlertTag
      hideBorder
      onClick={() => onClick(alert, index)}
      key={alert?.id}
      maxWidth="200px"
    >
      <SearchAlertTagText>
        <FormattedMessage id={alert?.name} />
      </SearchAlertTagText>
      <NotificationBadge>{alert?.newJobCount || 0}</NotificationBadge>
    </SearchAlertTag>
  ));
};

const SearchAlertTag = styled(Tag)`
  font-weight: 700;
  display: flex;
`;

const SearchAlertTagText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
